import { useTranslation } from "react-i18next";
import {
  CoRelationDependencyRules,
  DependencyRules,
  JsonSchemaItemProperty,
  JsonSchemaPropertyMap,
  RowData,
  RowDataObject,
} from "../../../../types";
import {
  getInputType,
  getIsDisabled,
  getResolvedDynamicRowDataValue,
  getTypeAheadRequest,
  getSelectableOptions,
  getTranslatedTooltip,
  getSelectedOption,
  getSelectedOptions,
  getDynamicDependentFieldsAndValues,
  getCorrelationPropertyContext,
  getObjectPropertyContext,
} from "../../helpers";
import { extractErrorAndMessage } from "../../validations";
import {
  CURRENCY_FIELD_KEYS,
  CUSTOM_MULTI_SELECT_REQUIRED_KEYS,
  IMPLEMENTED_SETUP_KEYS,
} from "../../constants";
import RenderAutoCompleteMenu from "./RenderAutoCompleteMenu";
import RenderCheckbox from "./RenderCheckbox";
import RenderInput from "./RenderInput";
import RenderCurrencyInput from "./RenderCurrencyInput";
import RenderMultiInput from "./RenderMultiInput";
import RenderMultiAutoCompleteMenu from "./RenderMultiAutoCompleteMenu";
import {
  DatePickerWithTooltip,
  DropdownWithTooltip,
  MegaDateTimePickerWithTooltip,
} from "../../../common/Tooltip";
import { CUSTOM_COMPONENTS } from "../custom-renderers/custom-fields/RenderCustomFields";
import i18n from "../../../../translate/i18next";
import { useUserContext } from "../../../../contexts/UserContext";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { useRowDataContext } from "../../../../contexts/RowDataContext";

export type DynamicAdvancedFieldRendererProps = {
  propertyKey: string;
  properties: JsonSchemaPropertyMap;
  required: string[];
  rowScopedProperties?: Record<number, JsonSchemaPropertyMap>;
  inputValueObject: RowDataObject[] | RowData;
  dependencyRules: DependencyRules | CoRelationDependencyRules | undefined;
  triggerDependencyRule: string | undefined;
  rowIndex: number | undefined;
  propertySubKey: string;
  itemProperty: JsonSchemaItemProperty;
  isDialogField?: boolean;
  isInline?: boolean;
};

export const DynamicAdvancedFieldRenderer = ({
  propertyKey,
  properties,
  required,
  rowScopedProperties,
  inputValueObject,
  dependencyRules,
  triggerDependencyRule,
  rowIndex,
  propertySubKey,
  itemProperty,
  isDialogField = false,
  isInline = false,
}: DynamicAdvancedFieldRendererProps): JSX.Element => {
  const { t } = useTranslation();
  const { selectedTabSectionKey } = useUIStateContext();
  const {
    iamUserState,
    setupBaseRequestInfo,
    selected,
    evaluatedResults,
    setupListNavInfoByDepartment,
  } = useUserContext();
  const { rowDataReducerState, dispatch } = useRowDataContext();
  const { rowData, isReadOnly } = rowDataReducerState;

  const translateWithFallback = (key: string, fallback: string) => {
    const namespaces = [
      selectedTabSectionKey,
      ...IMPLEMENTED_SETUP_KEYS,
    ].filter(Boolean) as string[];
    return t(`column.${key}`, { defaultValue: fallback, ns: namespaces });
  };

  const propertyContext =
    rowIndex !== undefined
      ? getCorrelationPropertyContext({
          properties,
          rowScopedProperties,
          propertySubKey,
          propertyKey,
          rowIndex,
          inputValueObject,
          rowData,
        })
      : getObjectPropertyContext({
          properties,
          propertyKey,
          propertySubKey,
          inputValueObject,
          rowData,
        });

  const {
    updatedItemProperty,
    inputValue,
    dynamicPropertyKey,
    dynamicRowData = rowData,
  } = propertyContext;

  const {
    title,
    description = "",
    type: propertyType,
    isStore,
    isGroupId,
    enum: enumValues,
    enumLabels,
    key: nestedPropertySubKey,
    companyKey,
    storeKey,
    dependentFields,
    typeaheadConfig,
    evalMethod,
    format,
    maxLength,
    minLength,
    maximum,
    minimum,
    maxItems,
  } = updatedItemProperty;

  const translatedTitle = i18n.language?.startsWith("en")
    ? title
    : translateWithFallback(propertySubKey, title);
  const translatedDescription = getTranslatedTooltip(
    propertySubKey,
    description,
    t,
    i18n.language,
    rowDataReducerState?.schema?.title,
  );
  const type = getInputType(propertyType);
  const isRequired = required.includes(propertySubKey);

  const isDisabled = getIsDisabled({
    property: updatedItemProperty,
    rowData,
    rowIndex,
    isReadOnly,
  });

  const existingStoreIdKey = typeaheadConfig?.storeId;
  let existingStoreIdValue: string | undefined;
  if (existingStoreIdKey) {
    existingStoreIdValue = getResolvedDynamicRowDataValue({
      propertyKey: dynamicPropertyKey,
      dynamicField: existingStoreIdKey,
      rowData: dynamicRowData,
      rowIndex,
      selectedTabSectionKey,
    }) as string;
  }

  const existingGroupIdKey = typeaheadConfig?.groupId;
  let existingGroupIdValue: string | undefined;
  if (existingGroupIdKey) {
    existingGroupIdValue = getResolvedDynamicRowDataValue({
      propertyKey: dynamicPropertyKey,
      dynamicField: existingGroupIdKey,
      rowData: dynamicRowData,
      rowIndex,
      selectedTabSectionKey,
    }) as string;
  }

  const typeAheadRequest = typeaheadConfig
    ? getTypeAheadRequest({
        propertyKey: dynamicPropertyKey,
        typeaheadConfig,
        rowIndex,
        rowDataReducerState,
        setupBaseRequestInfo,
        existingStoreIdValue,
        existingGroupIdValue,
        iamUserState,
        setupListNavInfoByDepartment,
      })
    : undefined;

  const { error, message } = extractErrorAndMessage(
    propertyKey,
    rowIndex,
    propertySubKey,
    rowDataReducerState,
    undefined,
  );

  const CustomComponent = propertySubKey
    ? CUSTOM_COMPONENTS[propertySubKey]
    : null;
  if (CustomComponent) {
    return (
      <CustomComponent
        propertyKey={propertyKey}
        propertySubKey={propertySubKey}
        rowIndex={rowIndex}
        inputValue={inputValue}
        evalMethod={evalMethod}
        rowDataReducerState={rowDataReducerState}
        dispatch={dispatch}
      />
    );
  }

  switch (type) {
    case "boolean":
      return (
        <RenderCheckbox
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          propertySubKey={propertySubKey}
          rowIndex={rowIndex}
          inputValue={inputValue}
          dispatch={dispatch}
          isDisabled={isDisabled}
          isDialogField={isDialogField}
          dependencyRules={dependencyRules}
          triggerDependencyRule={triggerDependencyRule}
          evalMethod={evalMethod}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
        />
      );

    case "string": {
      if (format === "dateTime") {
        return (
          <MegaDateTimePickerWithTooltip
            title={translatedTitle}
            description={translatedDescription}
            tooltip={translatedDescription}
            propertyKey={propertyKey}
            rowIndex={rowIndex}
            propertySubKey={propertySubKey}
            inputValue={inputValue as string}
            isRequired={isRequired}
            error={error}
            message={message}
            dispatch={dispatch}
            isDialogField={isDialogField}
            isDisabled={isDisabled}
            evalMethod={evalMethod}
            selectedTabSectionKey={selectedTabSectionKey}
          />
        );
      }

      if (format === "date") {
        return (
          <DatePickerWithTooltip
            title={translatedTitle}
            description={translatedDescription}
            tooltip={translatedDescription}
            propertyKey={propertyKey}
            propertySubKey={propertySubKey}
            rowIndex={rowIndex}
            inputValue={inputValue as string}
            isRequired={isRequired}
            error={error}
            message={message}
            isDialogField={isDialogField}
            dispatch={dispatch}
            isDisabled={isDisabled}
            evalMethod={evalMethod}
            selectedTabSectionKey={selectedTabSectionKey}
          />
        );
      }

      if (isStore || enumValues || isGroupId) {
        const optionsList = getSelectableOptions({
          propertyKey: propertySubKey,
          enumValues: enumValues ?? [],
          enumLabels,
          iamUserState,
          selected,
          isStore,
          isGroupId,
          selectedTabSectionKey,
          evaluatedResults,
          rowData,
          rowIndex,
          setupListNavInfoByDepartment,
        });
        const selectedOption = getSelectedOption({
          inputValue,
          optionsList,
        });

        return (
          <DropdownWithTooltip
            title={translatedTitle}
            description={translatedDescription}
            tooltip={isInline ? "" : translatedDescription}
            propertyKey={propertyKey}
            rowIndex={rowIndex}
            propertySubKey={propertySubKey}
            selectedOption={selectedOption}
            optionsList={optionsList}
            companyKey={companyKey}
            storeKey={storeKey}
            dependentFields={dependentFields}
            isRequired={isRequired}
            isDisabled={isDisabled}
            error={error}
            message={message}
            evalMethod={evalMethod}
            dependencyRules={dependencyRules as CoRelationDependencyRules}
            triggerDependencyRule={triggerDependencyRule}
            isDialogField={isDialogField}
            selectedTabSectionKey={selectedTabSectionKey}
            rowDataReducerState={rowDataReducerState}
            dispatch={dispatch}
            isGroupId={isGroupId}
            isInline={isInline}
          />
        );
      }

      if (typeAheadRequest) {
        return (
          <RenderAutoCompleteMenu
            title={translatedTitle}
            description={translatedDescription}
            propertyKey={propertyKey}
            rowIndex={rowIndex}
            propertySubKey={propertySubKey}
            inputValue={inputValue as string | number}
            isRequired={isRequired}
            isDisabled={isDisabled}
            error={error}
            message={message}
            typeAheadRequest={typeAheadRequest}
            companyKey={undefined}
            dependentFields={undefined}
            evalMethod={evalMethod}
            evalTypeAheadMethods={undefined}
            existingStoreIdKey={existingStoreIdKey}
            existingStoreIdValue={existingStoreIdValue}
            existingGroupIdKey={existingGroupIdKey}
            existingGroupIdValue={existingGroupIdValue}
            isDialogField={isDialogField}
            selectedTabSectionKey={selectedTabSectionKey}
            rowDataReducerState={rowDataReducerState}
            dispatch={dispatch}
            isInline={isInline}
          />
        );
      }

      return (
        <RenderInput
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          rowIndex={rowIndex}
          evalMethod={evalMethod}
          propertySubKey={propertySubKey}
          isDisabled={isDisabled}
          inputValue={inputValue}
          type={type}
          isRequired={isRequired}
          storeKey={storeKey}
          error={error}
          message={message}
          isDialogField={isDialogField}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
          maxLength={maxLength}
          minLength={minLength}
          maximum={maximum}
          minimum={minimum}
          isInline={isInline}
        />
      );
    }

    case "array": {
      const selectedOptions = getSelectedOptions(
        inputValue,
        nestedPropertySubKey,
      );
      if (typeAheadRequest && itemProperty.typeaheadConfig) {
        const dynamicDependentFieldsAndValues =
          getDynamicDependentFieldsAndValues(
            itemProperty.typeaheadConfig,
            propertySubKey,
            rowDataReducerState,
          );
        return (
          <RenderMultiAutoCompleteMenu
            title={translatedTitle}
            description={translatedDescription}
            propertyKey={propertyKey}
            propertySubKey={propertySubKey}
            rowIndex={rowIndex}
            nestedPropertySubKey={nestedPropertySubKey ?? propertySubKey}
            nestedRowIndex={rowIndex}
            inputValues={selectedOptions}
            optionsList={undefined}
            typeAheadRequest={typeAheadRequest}
            dynamicDependentFieldsAndValues={dynamicDependentFieldsAndValues}
            isRequired={CUSTOM_MULTI_SELECT_REQUIRED_KEYS.includes(propertyKey)}
            isDisabled={isDisabled}
            error={error}
            message={message}
            selectedTabSectionKey={selectedTabSectionKey}
            rowDataReducerState={rowDataReducerState}
            dispatch={dispatch}
            existingGroupIdKey={existingGroupIdKey}
            existingGroupIdValue={existingGroupIdValue}
            maxItems={maxItems}
            evalTypeAheadMethods={itemProperty.typeaheadConfig?.evalMethods}
            typeAheadConfig={itemProperty.typeaheadConfig}
          />
        );
      }
      return (
        <RenderMultiInput
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          propertySubKey={propertySubKey}
          nestedPropertySubKey={nestedPropertySubKey ?? propertySubKey}
          nestedRowIndex={rowIndex}
          inputValues={selectedOptions}
          isRequired={isRequired}
          isDisabled={isDisabled}
          error={error}
          message={message}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
    }

    default:
      if (CURRENCY_FIELD_KEYS.includes(propertySubKey)) {
        return (
          <RenderCurrencyInput
            title={translatedTitle}
            description={translatedDescription}
            propertyKey={propertyKey}
            rowIndex={rowIndex}
            evalMethod={evalMethod}
            propertySubKey={propertySubKey}
            isDisabled={isDisabled}
            inputValue={inputValue}
            type={type}
            isRequired={isRequired}
            storeKey={storeKey}
            error={error}
            message={message}
            isDialogField={isDialogField}
            selectedTabSectionKey={selectedTabSectionKey}
            rowDataReducerState={rowDataReducerState}
            dispatch={dispatch}
            isInline={isInline}
          />
        );
      }

      return (
        <RenderInput
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          rowIndex={rowIndex}
          propertySubKey={propertySubKey}
          evalMethod={evalMethod}
          isDisabled={isDisabled}
          inputValue={inputValue}
          type={type}
          isRequired={isRequired}
          storeKey={storeKey}
          error={error}
          message={message}
          isDialogField={isDialogField}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
          maxLength={maxLength}
          minLength={minLength}
          maximum={maximum}
          minimum={minimum}
          isInline={isInline}
        />
      );
  }
};
